import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IFiltroRecorteExtracao,
  ILayoutRecorte,
  ILayoutRecorteTable,
} from "../../../models";
import { IFormError, IFormProps } from "../../utils";

interface ICreateLayoutRecorteFormFields {
  Nome: string;
  Filtros: IFiltroRecorteExtracao[];
}

interface IEditLayoutRecorteFormFields{
  IdVelesGeoLayoutRecorte: number;
  Nome: string;
  Filtros: IFiltroRecorteExtracao[];
}

interface ILayoutRecorteState {
  layoutsRecorte: ILayoutRecorte[];
  Layouts: ILayoutRecorteTable[];
  layoutRecorteSelecionado?: ILayoutRecorteTable;
  createLayoutRecorteForm: IFormProps<ICreateLayoutRecorteFormFields>;
  createLayoutRecorteFormOpen: boolean;
  editLayoutRecorteForm: IFormProps<IEditLayoutRecorteFormFields>;
  editLayoutRecorteFormOpen: boolean;
  openDeleteLayoutRecorte: boolean;
}

const createLayoutRecorteFormFieldsInitialState: ICreateLayoutRecorteFormFields =
  {
    Nome: "",
    Filtros: [],
  };

const editLayoutRecorteFormFieldsInitialState: IEditLayoutRecorteFormFields = {
  IdVelesGeoLayoutRecorte: 0,
  Nome: "",
  Filtros: [],
};

const initialState: ILayoutRecorteState = {
  layoutsRecorte: [],
  Layouts: [],
  createLayoutRecorteForm: {
    errors: [],
    fields: createLayoutRecorteFormFieldsInitialState,
    touched: false,
  },
  createLayoutRecorteFormOpen: false,
  editLayoutRecorteForm: {
    errors: [],
    fields: editLayoutRecorteFormFieldsInitialState,
    touched: false,
  },
  editLayoutRecorteFormOpen: false,
  openDeleteLayoutRecorte: false,
};

export const validateCreateLayoutRecorteForm = (
  fields: ICreateLayoutRecorteFormFields
): IFormError<keyof ICreateLayoutRecorteFormFields>[] => {
  const errors: IFormError<keyof ICreateLayoutRecorteFormFields>[] = [];

  if (!fields.Nome) {
    errors.push({
      key: "Nome",
      message: "O nome do layout não pode ficar em branco",
    });
  } else if (
    fields.Nome.length === 0 ||
    fields.Nome.replace("  ", " ").trim().length === 0
  ) {
    errors.push({
      key: "Nome",
      message: "O nome do layout não pode ficar em branco",
    });
  } else if (fields.Filtros.length === 0) {
    errors.push({
      key: "Filtros",
      message: "Selecione ao menos um filtro",
    });
  }

  return errors;
};

export const validateEditLayoutRecorteForm = (
  fields: IEditLayoutRecorteFormFields
): IFormError<keyof IEditLayoutRecorteFormFields>[] => {
  const errors: IFormError<keyof IEditLayoutRecorteFormFields>[] = [];

  if (!fields.Nome) {
    errors.push({
      key: "Nome",
      message: "Nome é obrigatório",
    });
  } else if (
    fields.Nome.length === 0 ||
    fields.Nome.replace("  ", " ").trim().length === 0
  ) {
    errors.push({
      key: "Nome",
      message: "O nome do layout não pode ficar em branco",
    });
  } else if (fields.Filtros.length === 0) {
    errors.push({
      key: "Filtros",
      message: "Selecione ao menos um filtro",
    });
  } else if (fields.IdVelesGeoLayoutRecorte === 0) {
    errors.push({
      key: "IdVelesGeoLayoutRecorte",
      message: "É necessário o id do layout",
    });
  }

  return errors;
};

const layoutRecorteSlice = createSlice({
  name: "layoutRecorte",
  initialState,
  reducers: {
    setLayoutsRecorteState(
      state,
      action: PayloadAction<ILayoutRecorteTable[]>
    ) {
      state.Layouts = action.payload;
    },
    resetLayoutsRecorteState(state) {
      state.Layouts = initialState.Layouts;
    },
    resetLayoutRecorteSelecionadoState(state) {
      state.layoutRecorteSelecionado = initialState.layoutRecorteSelecionado;
    },
    setCreateLayoutRecorteFormOpenState(state, action: PayloadAction<boolean>) {
      state.createLayoutRecorteFormOpen = action.payload;
    },    
    setCreateLayoutRecorteFormFieldsNomeState(
      state,
      action: PayloadAction<string>
    ) {
      state.createLayoutRecorteForm.fields.Nome = action.payload;
    },
    setCreateLayoutRecorteFormFieldsFiltrosState(
      state,
      action: PayloadAction<IFiltroRecorteExtracao[]>
    ) {
      state.createLayoutRecorteForm.fields.Filtros = action.payload;
    },
    setCreateLayoutRecorteFormTouchedState(
      state,
      action: PayloadAction<boolean>
    ) {
      state.createLayoutRecorteForm.touched = action.payload;
    },
    setCreateLayoutRecorteFormErrorsState(
      state,
      action: PayloadAction<{
        errors: IFormError<keyof ICreateLayoutRecorteFormFields>[];
      }>
    ) {
      state.createLayoutRecorteForm.errors = action.payload.errors;
    },
    setCreateLayoutRecorteFormDeliveredState(
      state,
      action: PayloadAction<boolean>
    ) {
      state.createLayoutRecorteForm.delivered = action.payload;
    },
    resetCreateLayoutRecorteFormState(state) {
      state.createLayoutRecorteForm = initialState.createLayoutRecorteForm;
    },

    resetCreateLayoutRecorteFormNomeState(state) {
      state.createLayoutRecorteForm.fields.Nome =
        initialState.createLayoutRecorteForm.fields.Nome;
    },
    setEditLayoutRecorteFormFieldsIdVelesGeoLayoutRecorteState(
      state,
      action: PayloadAction<number>
    ) {
      state.editLayoutRecorteForm.fields.IdVelesGeoLayoutRecorte =
        action.payload;
    },
    setEditLayoutRecorteFormFieldsNomeState(
      state,
      action: PayloadAction<string>
    ) {
      state.editLayoutRecorteForm.fields.Nome = action.payload;
    },
    setEditLayoutRecorteFormFieldsFiltrosState(
      state,
      action: PayloadAction<IFiltroRecorteExtracao[]>
    ) {
      state.editLayoutRecorteForm.fields.Filtros = action.payload;
    },
    setEditLayoutRecorteFormTouchedState(
      state,
      action: PayloadAction<boolean>
    ) {
      state.editLayoutRecorteForm.touched = action.payload;
    },
    setEditLayoutRecorteFormErrorsState(
      state,
      action: PayloadAction<{
        errors: IFormError<keyof IEditLayoutRecorteFormFields>[];
      }>
    ) {
      state.editLayoutRecorteForm.errors = action.payload.errors;
    },
    setEditLayoutRecorteFormDeliveredState(
      state,
      action: PayloadAction<boolean>
    ) {
      state.editLayoutRecorteForm.delivered = action.payload;
    },
    resetEditLayoutRecorteFormState(state) {
      state.editLayoutRecorteForm = initialState.editLayoutRecorteForm;
    },
    setEditLayoutRecorteFormOpenState(state, action: PayloadAction<boolean>) {
      state.editLayoutRecorteFormOpen = action.payload;
    },
    setOpenDeleteLayoutRecorteState(state, action: PayloadAction<boolean>) {
      state.openDeleteLayoutRecorte = action.payload;
    },
    setLayoutRecorteSelecionadoState(state, action: PayloadAction<ILayoutRecorteTable>) {     
      state.layoutRecorteSelecionado = action.payload;
    }
  },
});

export const {
  resetCreateLayoutRecorteFormState,
  resetEditLayoutRecorteFormState,
  resetLayoutRecorteSelecionadoState,
  resetLayoutsRecorteState,
  setCreateLayoutRecorteFormDeliveredState,
  setCreateLayoutRecorteFormErrorsState,
  setCreateLayoutRecorteFormTouchedState,
  setCreateLayoutRecorteFormFieldsFiltrosState,
  setCreateLayoutRecorteFormFieldsNomeState,
  setEditLayoutRecorteFormDeliveredState,
  setEditLayoutRecorteFormErrorsState,
  setEditLayoutRecorteFormTouchedState,
  setEditLayoutRecorteFormFieldsFiltrosState,
  setEditLayoutRecorteFormFieldsIdVelesGeoLayoutRecorteState,
  setEditLayoutRecorteFormFieldsNomeState,
  setLayoutsRecorteState,
  setCreateLayoutRecorteFormOpenState,
  setEditLayoutRecorteFormOpenState,
  setOpenDeleteLayoutRecorteState,
  resetCreateLayoutRecorteFormNomeState,
  //setLayoutRecortesState,
  setLayoutRecorteSelecionadoState,
} = layoutRecorteSlice.actions;

export const layoutRecorteReducer = layoutRecorteSlice.reducer;
